import {ApolloQueryResult, gql} from '@apollo/client';
import {naturalSortByKey} from '@benchling/js-utils';
import {createStyles} from '@mantine/core';
import React, {useEffect, useMemo, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {coreObjectArchiveAction} from 'app-core/platform/archivable/CoreObjectArchiveActions';
import {getUnarchiveAction} from 'archive/ArchiveActionsUtil';
import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import {assert} from 'Asserts';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {FieldDefinitionType, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import FieldActionButtons from 'metadata/FieldActionButtons';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  CREATE_LIBRARY_PARAMETER_DEFINITION_ENDED_EVENT,
  CREATE_LIBRARY_PARAMETER_DEFINITION_STARTED_EVENT,
} from 'performance/processProceduresEvents';
import {
  MAX_PARAMETER_DESCRIPTION_LENGTH,
  MAX_PARAMETER_NAME_LENGTH,
} from 'procedures/design/ProcedureDesignConstants';
import {getParameterNameSqlIdentifierErrorMessage} from 'procedures/design/validation/private-validation-helpers/validateParameters';
import {useTimedAttentionIds} from 'procedures/hooks/useTimedAttentionIds';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import getTestProps from 'test/lib/cypress/getTestProps';
import FormInput from 'ui-library/bs/FormInput';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import {ButtonSize} from 'ui-library/buttons/Button';
import {GRAY, GRAY_LIGHTER, NEAR_BLACK} from 'ui-library/Colors';
import ErrorMessage from 'ui-library/ErrorMessage';
import FieldError from 'ui-library/FieldError';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useDraftState from 'ui-library/hooks/useDraftState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {CenteredSpinner} from 'ui-library/Spinner';
import SearchableUnitTypeDropdown from 'units/SearchableUnitTypeDropdown';
import * as Keyboard from 'util/Keyboard';
import strUtil from 'util/strUtil';
import {InputChangeEvent} from 'util/Types';

import {createLibraryParameterDefinition} from '../network/LibraryParameterDefinitionDAO';
import {
  isLibraryParameterDefinitionDataType,
  LibraryParameterDefinition,
  LibraryParameterDefinitionDataType,
} from '../ParameterLibraryTypes';
import {
  ParameterLibraryListingQuery,
  ParameterLibraryListingQueryVariables,
} from './__generated/ParameterLibraryListingQuery';
import {LibraryParameterDefinitionDataTypeSelector} from './LibraryParameterDefinitionDataTypeSelector';

const useStyles = createStyles(() => ({
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  archivedField: {
    opacity: 0.6,
  },
  tableControls: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    marginTop: '6px',
    marginBottom: '16px',
    alignItems: 'center',
  },
  search: {
    width: 300,
    verticalAlign: 'middle',
    marginRight: '10px',
  },
  filteringControls: {
    display: 'flex',
  },
  addButton: {
    marginLeft: '10px',
  },
  auditAndAddButtons: {
    display: 'flex',
  },
  descriptionCell: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    minWidth: 0,
    maxWidth: '100%',
  },
  newlyCreatedParameterDefinitionRow: {
    '& > td': {
      backgroundColor: '#f1f3f5',
    },
  },
  emptyTableCell: {
    // Zero out the default .simpleTable-cell padding so the inner div fills the full cell.
    '& .simpleTable-row--empty .simpleTable-cell': {
      padding: 0,
      backgroundColor: GRAY_LIGHTER,
    },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: 220,
    gap: 10,
  },
  emptyStateIcon: {
    fontSize: 52,
    color: GRAY,
  },
  emptyStateText: {
    fontSize: 14,
    color: NEAR_BLACK,
    width: 250,
    textAlign: 'center',
    whiteSpace: 'normal',
  },
}));

const NEW_PARAMETER_DEFINITION_HIGHLIGHT_DURATION_MS = 10_000;
export const NEWLY_CREATED_PARAMETER_DEFINITION_ROW_CLASS =
  'parameterLibraryListing-newlyCreatedParameterDefinitionRow';

/** @package */
export const LIBRARY_PARAMETER_DEFINITION_DATA_TYPE_TO_FRIENDLY_NAME: Record<
  LibraryParameterDefinitionDataType,
  string
> = {
  [FieldDefinitionType.FLOAT]: 'Decimal',
  [FieldDefinitionType.TEXT]: 'Text',
  [FieldDefinitionType.DATETIME]: 'Datetime',
};

export const PARAMETER_LIBRARY_LISTING_QUERY = gql`
  query ParameterLibraryListingQuery @skipAudit @engComponent(name: PROCEDURES) {
    allLibraryParameterDefinitions {
      ...LibraryParameterDefinitionGraphQL
    }
    currentTenant {
      id
      permissions {
        canCreateLibraryParameterDefinitions
      }
    }
  }
  ${getFragmentWrapper('LibraryParameterDefinition').fragment}
`;

interface DraftLibraryParameterDefinition {
  name: string;
  dataType: LibraryParameterDefinitionDataType;
  description: string;
  unitTypeId: string | null;
}

const EMPTY_PARAMETER_DEFINITION: DraftLibraryParameterDefinition = {
  name: '',
  dataType: FieldDefinitionType.FLOAT,
  description: '',
  unitTypeId: null,
};

export default function ParameterLibraryListing(): JSX.Element {
  const queryResult = useBenchlingQuery<ParameterLibraryListingQuery, ParameterLibraryListingQueryVariables>(
    PARAMETER_LIBRARY_LISTING_QUERY,
    {renderCachedDataWhenRefetching: true}
  );

  useEffect(() => {
    if (queryResult.type === QueryResultType.SUCCESS) {
      queryResult.data.allLibraryParameterDefinitions.forEach((parameterDefinition) => {
        assert(
          isLibraryParameterDefinitionDataType(parameterDefinition.dataType),
          `Unexpected data type for library parameter definition: ${parameterDefinition.dataType}`
        );
      });
    }
  }, [queryResult]);

  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }
  if (queryResult.type === QueryResultType.ERROR) {
    return <ErrorMessage error="Failed to load parameter library" />;
  }
  return (
    <ParameterLibraryListingDisplay
      parameterDefinitions={
        queryResult.data.allLibraryParameterDefinitions as unknown as Array<LibraryParameterDefinition>
      }
      refetch={queryResult.refetch}
      canCreateLibraryParameterDefinitions={
        queryResult.data.currentTenant.permissions.canCreateLibraryParameterDefinitions
      }
    />
  );
}

/**
 * Presentational component for parameter library listing.
 * @package
 */
export function ParameterLibraryListingDisplay({
  parameterDefinitions,
  canCreateLibraryParameterDefinitions,
  refetch,
}: {
  parameterDefinitions: Array<LibraryParameterDefinition>;
  canCreateLibraryParameterDefinitions: boolean;
  refetch: () => Promise<ApolloQueryResult<ParameterLibraryListingQuery>>;
}): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('');
  const {markIdsForAttention: markParameterDefinitionRowsForAttention, shouldDrawAttention} =
    useTimedAttentionIds(NEW_PARAMETER_DEFINITION_HIGHLIGHT_DURATION_MS);
  const [selectedArchivePurposes, selectArchivePurposes] = useState<Array<string>>([
    NOT_ARCHIVED_PURPOSE_STRING,
  ]);
  // Used to manage state for the row being created or updated
  const [isSaving, setIsSaving] = useState(false);
  const draftState = useDraftState<DraftLibraryParameterDefinition>(EMPTY_PARAMETER_DEFINITION);
  const {
    data: draftParameterDefinition,
    updateDraftData: updateDraftParameterDefinition,
    isEditing,
    startEditing,
    stopEditing,
  } = draftState;

  const {classes} = useStyles();
  const trimmedSearchQuery = searchQuery.trim().toLowerCase();
  const isSearching = !strUtil.isEmpty(trimmedSearchQuery);

  const allParameterDefinitionsIds = parameterDefinitions.map((pd) => pd.id);

  const sortedParameterDefinitions = naturalSortByKey(parameterDefinitions, 'name');
  const parameterDefinitionsMatchingQuery = isSearching
    ? sortedParameterDefinitions.filter((pd) => pd.name.toLowerCase().includes(trimmedSearchQuery))
    : sortedParameterDefinitions;
  const filteredParameterDefinitions = parameterDefinitionsMatchingQuery.filter((pd) =>
    pd.archiveData.archived
      ? selectedArchivePurposes.includes(pd.archiveData.purpose!)
      : selectedArchivePurposes.includes(NOT_ARCHIVED_PURPOSE_STRING)
  );
  const existingNames = useMemo(
    () => new Set(parameterDefinitions.map((parameterDefinition) => parameterDefinition.name)),
    [parameterDefinitions]
  );

  const getEmptyTableContent = (): JSX.Element => {
    const recipeLabel = getProcedureUIString(ProceduresStringKeys.PROCEDURE, ProcedureType.RECIPE, {
      stringCase: StringCase.LOWER,
      plural: true,
    });
    const assayLabel = getProcedureUIString(ProceduresStringKeys.PROCEDURE, ProcedureType.ASSAY, {
      stringCase: StringCase.LOWER,
      plural: true,
    });
    const parts: Array<string> = [];
    if (isFeatureEnabledForProcedureType(ProcedureType.RECIPE, ProceduresFeature.OPEN)) {
      parts.push(recipeLabel);
    }
    if (isFeatureEnabledForProcedureType(ProcedureType.ASSAY, ProceduresFeature.OPEN)) {
      parts.push(assayLabel);
    }
    const text =
      parts.length === 0
        ? 'Build a standardized list of parameters here.'
        : `Build a standardized list of parameters here for your ${parts.length === 1 ? parts[0] : `${parts[0]} and ${parts[1]}`}.`;
    return (
      <div className={classes.emptyState}>
        <BenchlingIcon icon="parameters" className={classes.emptyStateIcon} />
        <span className={classes.emptyStateText}>{text}</span>
      </div>
    );
  };

  /**
   * Translates a table row index to the corresponding index in filteredParameterDefinitions.
   * When editing, the draft row occupies index 0, so existing data rows are shifted by 1.
   */
  function getDataRowIndex(rowIndex: number): number {
    // TODO (BNCH-133006): Update this to distinguish between creates and updates when editing an existing row.
    return isEditing ? rowIndex - 1 : rowIndex;
  }

  function isNewParameterDefinitionRow(rowIndex: number): boolean {
    return isEditing && rowIndex === 0;
  }

  function getArchivedClass(rowIndex: number): string | undefined {
    if (isNewParameterDefinitionRow(rowIndex)) {
      return undefined;
    }
    return filteredParameterDefinitions[getDataRowIndex(rowIndex)].archiveData.archived
      ? classes.archivedField
      : undefined;
  }

  function getRowClassName(rowIndex: number): string {
    if (isNewParameterDefinitionRow(rowIndex)) {
      return '';
    }

    return shouldDrawAttention(filteredParameterDefinitions[getDataRowIndex(rowIndex)].id)
      ? `${classes.newlyCreatedParameterDefinitionRow} ${NEWLY_CREATED_PARAMETER_DEFINITION_ROW_CLASS}`
      : '';
  }

  function onArchive(parameterDefinition: LibraryParameterDefinition): void {
    ArchiveModal.open({
      items: [parameterDefinition],
      onArchive: async (itemIds: Array<string>, purpose: string): Promise<void> => {
        await coreObjectArchiveAction(itemIds, purpose);
      },
    });
  }

  async function onUnarchive(parameterDefinition: LibraryParameterDefinition): Promise<void> {
    const unarchiveFn = getUnarchiveAction([parameterDefinition.id]);
    await unarchiveFn([parameterDefinition.id]);
  }

  function onCancel(): void {
    if (isSaving) {
      return;
    }
    stopEditing();
    updateDraftParameterDefinition(EMPTY_PARAMETER_DEFINITION);
  }

  function getParameterNameErrorMessage(name: string): string | null {
    if (name.length > MAX_PARAMETER_NAME_LENGTH) {
      return 'Name is too long';
    }

    // TODO (BNCH-133006): Make sure this doesn't give us a false positive with the parameter being updated when we support updates.
    const nameAlreadyInUse = existingNames.has(name);
    if (nameAlreadyInUse) {
      return 'Name already in use';
    }
    return getParameterNameSqlIdentifierErrorMessage(name, 'Parameter');
  }

  const trimmedDraftParameterDefinitionName = draftParameterDefinition.name.trim();
  const nameErrorMessage = getParameterNameErrorMessage(trimmedDraftParameterDefinitionName);
  const descriptionExceedsMaxLength =
    draftParameterDefinition.description.length > MAX_PARAMETER_DESCRIPTION_LENGTH;
  const descriptionErrorMessage = descriptionExceedsMaxLength ? 'Description is too long' : null;
  const isValid =
    !strUtil.isEmpty(draftParameterDefinition.name) &&
    nameErrorMessage == null &&
    !descriptionExceedsMaxLength;

  async function onAddParameterDefinition(): Promise<void> {
    if (isSaving) {
      return;
    }
    const profileEventId = uuidv4();
    logProfilerTimingEvent({...CREATE_LIBRARY_PARAMETER_DEFINITION_STARTED_EVENT, id: profileEventId});
    const parameterDefinitionToCreate: DraftLibraryParameterDefinition = {
      ...draftParameterDefinition,
      name: trimmedDraftParameterDefinitionName,
    };
    setIsSaving(true);
    try {
      const createResult = await createLibraryParameterDefinition(parameterDefinitionToCreate);
      stopEditing();
      await refetch();
      markParameterDefinitionRowsForAttention(createResult.createdIds);
      updateDraftParameterDefinition(EMPTY_PARAMETER_DEFINITION);
      notifications.success(
        <>
          Parameter <strong>{trimmedDraftParameterDefinitionName}</strong> added
        </>
      );
      logProfilerTimingEvent({...CREATE_LIBRARY_PARAMETER_DEFINITION_ENDED_EVENT, id: profileEventId});
    } finally {
      setIsSaving(false);
    }
  }

  async function handleNewRowKeyDown(e: React.KeyboardEvent<HTMLInputElement>): Promise<void> {
    if (Keyboard.isOSCtrlKey(e) && Keyboard.isEnterKey(e) && isValid && !isSaving) {
      e.preventDefault();
      await onAddParameterDefinition();
    }
  }

  function renderNameCell(rowIndex: number): JSX.Element {
    if (isNewParameterDefinitionRow(rowIndex)) {
      return (
        <Cell>
          <FormInput
            type="text"
            autoFocus={true}
            value={draftParameterDefinition.name}
            onChange={(e) => {
              updateDraftParameterDefinition({name: e.target.value});
            }}
            onKeyDown={handleNewRowKeyDown}
            error={nameErrorMessage ?? undefined}
            dataTestAttrs={{component: 'ParameterLibraryListing', element: 'nameInput'}}
          />
          <FieldError message={nameErrorMessage} compact={true} />
        </Cell>
      );
    }
    const dataRowIndex = getDataRowIndex(rowIndex);
    return (
      <Cell className={getArchivedClass(rowIndex)}>
        {filteredParameterDefinitions[dataRowIndex].name}
        {filteredParameterDefinitions[dataRowIndex].archiveData.archived
          ? ` (${filteredParameterDefinitions[dataRowIndex].archiveData.purpose})`
          : ''}
      </Cell>
    );
  }

  function renderDataTypeCell(rowIndex: number): JSX.Element {
    if (isNewParameterDefinitionRow(rowIndex)) {
      return (
        <Cell>
          <LibraryParameterDefinitionDataTypeSelector
            selectedType={draftParameterDefinition.dataType}
            onChangeSelection={(newType) => {
              updateDraftParameterDefinition({
                dataType: newType,
                // Clear unit type if switching to text
                unitTypeId: newType === FieldDefinitionType.TEXT ? null : draftParameterDefinition.unitTypeId,
              });
            }}
          />
        </Cell>
      );
    }
    const dataRowIndex = getDataRowIndex(rowIndex);
    return (
      <Cell className={getArchivedClass(rowIndex)}>
        {
          LIBRARY_PARAMETER_DEFINITION_DATA_TYPE_TO_FRIENDLY_NAME[
            filteredParameterDefinitions[dataRowIndex].dataType
          ]
        }
      </Cell>
    );
  }

  function renderUnitTypeCell(rowIndex: number): JSX.Element {
    if (isNewParameterDefinitionRow(rowIndex)) {
      return (
        <Cell>
          <SearchableUnitTypeDropdown
            selectedId={draftParameterDefinition.unitTypeId}
            onSelect={(selectedUnitType) => {
              updateDraftParameterDefinition({
                unitTypeId: selectedUnitType?.id ?? null,
              });
            }}
            includeNone={true}
            disabled={draftParameterDefinition.dataType !== FieldDefinitionType.FLOAT}
          />
        </Cell>
      );
    }
    const dataRowIndex = getDataRowIndex(rowIndex);
    return (
      <Cell className={getArchivedClass(rowIndex)}>
        {filteredParameterDefinitions[dataRowIndex].unitType?.name ?? ''}
      </Cell>
    );
  }

  function renderDescriptionCell(rowIndex: number): JSX.Element {
    const descriptionContent = isNewParameterDefinitionRow(rowIndex) ? (
      <>
        <FormInput
          type="text"
          value={draftParameterDefinition.description}
          onChange={(e) => {
            updateDraftParameterDefinition({description: e.target.value});
          }}
          onKeyDown={handleNewRowKeyDown}
          error={descriptionErrorMessage ?? undefined}
          dataTestAttrs={{component: 'ParameterLibraryListing', element: 'descriptionInput'}}
        />
        <FieldError message={descriptionErrorMessage} compact={true} />
      </>
    ) : (
      filteredParameterDefinitions[getDataRowIndex(rowIndex)].description
    );
    return (
      <Cell className={getArchivedClass(rowIndex)}>
        <div className={classes.descriptionCell}>{descriptionContent}</div>
      </Cell>
    );
  }

  function renderFieldActionButtonsCell(rowIndex: number): JSX.Element {
    if (isNewParameterDefinitionRow(rowIndex)) {
      return (
        <Cell>
          <FieldActionButtons
            className={classes.actionButtons}
            isEditing={true}
            unarchive={undefined}
            archive={undefined}
            cancel={onCancel}
            complete={onAddParameterDefinition}
            isCancelEnabled={isEditing && !isSaving}
            isCompleteEnabled={isEditing && isValid && !isSaving}
          />
        </Cell>
      );
    }
    const dataRowIndex = getDataRowIndex(rowIndex);
    const parameterDefinition = filteredParameterDefinitions[dataRowIndex];
    return (
      <Cell>
        <FieldActionButtons
          className={classes.actionButtons}
          isEditing={false}
          unarchive={
            parameterDefinition.archiveData.archived
              ? () => {
                  onUnarchive(parameterDefinition);
                }
              : undefined
          }
          isUnarchiveEnabled={!isEditing}
          archive={
            !parameterDefinition.archiveData.archived
              ? () => {
                  onArchive(parameterDefinition);
                }
              : undefined
          }
          isArchiveEnabled={!isEditing}
        />
      </Cell>
    );
  }

  return (
    <div {...getTestProps({component: 'ParameterLibraryListing', element: 'root'})}>
      <div className={classes.tableControls}>
        <div className={classes.filteringControls}>
          <PlainSearchInput
            className={classes.search}
            clearType={ClearType.X}
            onChange={(e: InputChangeEvent) => {
              setSearchQuery(e.target.value);
            }}
            onEscape={() => {
              setSearchQuery('');
            }}
            placeholder="Search"
            value={searchQuery}
          />
          <ArchivePurposeDropdown
            itemIds={allParameterDefinitionsIds}
            selectedPurposes={selectedArchivePurposes}
            onChangePurposes={(newPurposes) => {
              selectArchivePurposes(newPurposes);
            }}
          />
        </div>
        <div className={classes.auditAndAddButtons}>
          <Tooltip text={EXPORT_AUDIT_LOG_DISPLAY_STRING}>
            <Button
              icon="audit-trail"
              onClick={() => {
                exportAuditLogs(allParameterDefinitionsIds);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
            />
          </Tooltip>
          {canCreateLibraryParameterDefinitions && (
            <Button
              buttonStyle={ButtonStyle.PRIMARY}
              size={ButtonSize.SMALL}
              text="Add"
              icon="compose"
              className={classes.addButton}
              onClick={() => {
                startEditing();
              }}
              disabled={isEditing}
              dataTestAttrs={{component: 'ParameterLibraryListing', element: 'addButton'}}
            />
          )}
        </div>
      </div>
      <Table
        className={classes.emptyTableCell}
        frozenHeader={true}
        overflowVisible={true}
        showBorder={false}
        getRowClassName={getRowClassName}
        // TODO (BNCH-133006): Check if we're creating vs updating here
        numRows={isEditing ? filteredParameterDefinitions.length + 1 : filteredParameterDefinitions.length}
        emptyTableText={getEmptyTableContent()}
      >
        <Column header="Name" renderCell={renderNameCell} />
        <Column header="Data type" renderCell={renderDataTypeCell} />
        <Column header="Unit type" renderCell={renderUnitTypeCell} />
        <Column header="Description" renderCell={renderDescriptionCell} />
        <Column
          // no header for action buttons
          header=""
          renderCell={renderFieldActionButtonsCell}
        />
      </Table>
    </div>
  );
}
